import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ResultsContainer from "./results-container";
import SearchBar from "./search-bar";

import styles from "./search-bar.module.scss";

/**
 * PageSearchBar component
 *
 * @param {Object} props - The component props
 * @param {string} props.query - The current search query
 * @param {Function} props.clearQuery - Function to clear the search query
 * @param {Object} props.querySuggestions - The search query suggestions object
 *  @param {Array} props.querySuggestions.hits - The array of search query suggestions
 * @param {boolean} props.shouldFocus - Flag to determine if the search bar should be focused
 * @param {Function} props.shouldFocusCallback - Callback function to be called after the search bar is focused by variable above
 * @param {boolean} props.shouldBlur - Flag to determine if the search bar should be blurred
 * @param {Function} props.shouldBlurCallback - Callback function to be called after the search bar is blurred by variable above
 * @param {Function} props.onPopulateQueryButtonClick - Callback function to be called when a suggestion is clicked
 * @param {Function} props.onSearch - Function to perform a search. It takes the query and suggestionId as arguments.
 * @param {Function} props.onInputChange - Function to handle input change. It takes the event as an argument.
 * @param {Function} props.onInputKeyDown - Function to handle key down events on the input. It takes the event as an argument. Should handle the "enter" key event.
 *
 * @returns {JSX.Element} The rendered PageSearchBar component
 */
const PageSearchBar = ({
  query = "",
  clearQuery = () => {},
  querySuggestions = { hits: [] },
  shouldFocus = false,
  shouldFocusCallback = () => {},
  shouldBlur = false,
  shouldBlurCallback = () => {},
  onPopulateQueryButtonClick = (newQuery) => {},
  onSearch = (newQuery, suggestionId, index) => {},
  onInputChange = (event) => {},
  onInputKeyDown = (event) => {}
}) => {
  const [focused, setFocused] = useState(false);
  const searchFormContainerRef = useRef(null);

  // Handlers for searches that utilize the onSearch function.
  const onResultSearchButtonClick = (newQuery, suggestionId, index) => {
    console.log("onResultSearchButtonClick", newQuery, suggestionId);
    onSearch(newQuery, suggestionId, index);
  };
  const onSearchButtonClick = (event) => {
    console.log("onSearchButtonClick", query);
    event.preventDefault();
    onSearch(query);
  };

  // Handlers for focusing and blurring.
  useEffect(() => {
    if (shouldFocus) {
      setFocused(true);
      searchFormContainerRef.current.focus();
      shouldFocusCallback();
    }
  }, [shouldFocus, shouldFocusCallback]);

  useEffect(() => {
    if (shouldBlur) {
      setFocused(false);
      searchFormContainerRef.current.blur();
      shouldBlurCallback();
    }
  }, [shouldBlur, shouldBlurCallback]);

  // Add a handler to track when the user clicks outside the search form
  useEffect(() => {
    const onClickHandler = (event) => {
      if (!searchFormContainerRef.current.contains(event.target)) {
        setFocused(false);
      }
    };

    window.addEventListener("click", onClickHandler);
    return () => window.removeEventListener("click", onClickHandler);
  }, []);

  return (
    <>
      <div className={`${focused ? "d-block" : "d-none"} ${styles.placeholderSearchInputContainer}`} />

      <div
        ref={searchFormContainerRef}
        className={`col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6 ${
          focused ? styles.searchFormContainerFocused : styles.searchFormContainerBlurred
        } border-radius-6`}
      >
        <p className="mb-0 mt-2 h5 fw-bold" style={{ paddingLeft: 3 }}>
          Search WaFd Bank
        </p>
        <form className="row" autoComplete="off">
          <div className={`col-12 row my-2 ${query === "" ? "mb-3" : ""} mx-0 px-0`}>
            <div className={`d-flex flex-grow-1 px-3 ${styles.searchPageInputContainer}`}>
              <SearchBar
                query={query}
                clearQuery={clearQuery}
                onInputChange={onInputChange}
                onInputKeyDown={onInputKeyDown}
                onSearchButtonClick={onSearchButtonClick}
                id="ssi"
                label="Search WaFd Bank"
              />
            </div>
          </div>
        </form>
        <ResultsContainer
          onResultSearchButtonClick={onResultSearchButtonClick}
          onPopulateQueryButtonClick={onPopulateQueryButtonClick}
          focused={focused}
          query={query}
          querySuggestions={querySuggestions}
        />
      </div>
    </>
  );
};

export default PageSearchBar;

PageSearchBar.propTypes = {
  query: PropTypes.string.isRequired,
  clearQuery: PropTypes.func.isRequired,
  querySuggestions: PropTypes.object.isRequired,
  shouldFocus: PropTypes.bool.isRequired,
  shouldFocusCallback: PropTypes.func.isRequired,
  shouldBlur: PropTypes.bool.isRequired,
  shouldBlurCallback: PropTypes.func.isRequired,
  onPopulateQueryButtonClick: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onInputKeyDown: PropTypes.func.isRequired
};
